import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  useMediaQuery,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
  useTheme,
  Skeleton,
  Dialog,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import {
  useGetMeetingCategoryQuery,
  useGetMeetingHolidayQuery,
  useCreateMeetingCategoryMutation,
  useCreateMeetingHolidayMutation,
  useDeleteMeetingCategoryMutation,
  useDeleteMeetingHolidayMutation,
  useGetAllHolydaysArrayQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  IMeetingCategory,
  IMeetingHoliday,
  ITeam,
} from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate, useSearchParams } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import DeletePopup from "../../../Components/Delete/DeletePopup";
// import dayjs, { Dayjs } from "dayjs";
import DatePicker, { DateObject } from "react-multi-date-picker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";

interface HolidayFormValues {
  holidays: DateObject[];
  note: string;
}

const lightThemeColor = JSON.parse(
  localStorage.getItem("lightThemeColor") || "{}"
);

const setCardBRadius = lightThemeColor?.cardBorderRadius || "none";

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchParams] = useSearchParams();
  const paymentMonthYear = searchParams.get("date");
  const navigate = useNavigate();

  const [searchQuery1, setSearchQuery1] = useState("");
  const [currentSearchQuery1, setCurrentSearchQuery1] = useState("");
  const [page1, setPage1] = useState(0);
  const [rowsPerPage1, setRowsPerPage1] = useState(100);
  const debouncedSearchQuery1 = useDebounce(searchQuery1, 300);

  const [searchQuery2, setSearchQuery2] = useState("");
  const [currentSearchQuery2, setCurrentSearchQuery2] = useState("");
  const [page2, setPage2] = useState(0);
  const [rowsPerPage2, setRowsPerPage2] = useState(100);
  const debouncedSearchQuery2 = useDebounce(searchQuery2, 300);

  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] =
    useState<IMeetingCategory | null>(null);

  const [openEditCategory, setOpenEditCategory] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<IMeetingCategory | null>(
    null
  );

  const [openEditHoliday, setOpenEditHoliday] = useState(false);
  const [categoryToEditHoliday, setCategoryToEditHoliday] =
    useState<IMeetingHoliday | null>(null);

  const [openDeleteHoliday, setOpenDeleteHoliday] = useState(false);
  const [holidayToDelete, setHolidayToDelete] =
    useState<IMeetingHoliday | null>(null);

  const [openPicker, setOpenPicker] = useState(false);

  const [newCategory, { isLoading: createCategoryIsLoading }] =
    useCreateMeetingCategoryMutation();
  const [newHoliday, { isLoading: createHolidayIsLoading }] =
    useCreateMeetingHolidayMutation();

  const {
    data: getAllMeetingCategoryData,
    isLoading: meetingCategoryIsLoading,
    refetch: meetingCategoryRefetch,
    isError: meetingCategoryIsError,
  } = useGetMeetingCategoryQuery({
    searchText: currentSearchQuery1,
  });

  const {
    data: getAllMeetingHolidayData,
    isLoading: meetingHolidayIsLoading,
    refetch: meetingHistoryRefetch,
    isError: meetingHolidayIsError,
  } = useGetMeetingHolidayQuery({
    searchText: currentSearchQuery2,
  });

  const { data: holydaysData, isLoading: holidaysIsLoading } =
    useGetAllHolydaysArrayQuery({
      searchText: "",
      id: "",
    });

  useEffect(() => {
    if (debouncedSearchQuery1.length >= 4 || debouncedSearchQuery1 === "") {
      setCurrentSearchQuery1(debouncedSearchQuery1);
      meetingCategoryRefetch();
    }
  }, [debouncedSearchQuery1]);

  useEffect(() => {
    if (debouncedSearchQuery2.length >= 4 || debouncedSearchQuery2 === "") {
      setCurrentSearchQuery2(debouncedSearchQuery2);
      meetingHistoryRefetch();
    }
  }, [debouncedSearchQuery2]);

  const formik = useFormik({
    initialValues: {
      name: "",
      color: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          name: values.name,
          color: values.color,
        };
        const addCompanyResponse = await newCategory(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formikHoliday = useFormik<HolidayFormValues>({
    initialValues: {
      holidays: [],
      note: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          holidays: values.holidays.map((d: DateObject) =>
            d.format("YYYY-MM-DD")
          ),
          note: values.note,
        };
        const addCompanyResponse = await newHoliday(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formikEditCategory = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: categoryToEdit?.id,
      name: categoryToEdit?.name,
      color: categoryToEdit?.color,
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: values.id,
          name: values.name,
          color: values.color,
        };
        const addCompanyResponse = await newCategory(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formikHolidayEdit = useFormik({
    enableReinitialize: true,
    initialValues: {
      // holidays: categoryToEditHoliday?.holidays,
      holidays: categoryToEditHoliday?.holidays
        ? categoryToEditHoliday.holidays.map(
            (dateStr: string) => new DateObject(dateStr)
          )
        : [],
      note: categoryToEditHoliday?.note,
      id: categoryToEditHoliday?.id,
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          holidays: values.holidays.map((d: DateObject) =>
            d.format("YYYY-MM-DD")
          ),
          note: values.note,
          id: values.id,
        };
        const addCompanyResponse = await newHoliday(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleCloseHoliday();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleAddProductClick = () => {
    navigate("/client/meeting");
  };

  // useEffect(() => {
  //   if (categoryToEdit) {
  //     formikEditCategory.setValues({
  //       id: categoryToEdit.id,
  //       name: categoryToEdit.name,
  //       color: categoryToEdit.color,
  //     });
  //   }
  // }, [categoryToEdit]);

  const formValid = useMemo(() => {
    return formik.values.name === "" || formik.values.color === ""
      ? false
      : true;
  }, [formik.values]);

  const formValidHoliday = useMemo(() => {
    return formikHoliday.values.holidays.length > 0;
  }, [formikHoliday.values.holidays]);

  const formEditCategoryValid = useMemo(() => {
    return formikEditCategory.values.name === "" ||
      formikEditCategory.values.color === ""
      ? false
      : true;
  }, [formikEditCategory.values]);

  const formValidHolidayEdit = useMemo(() => {
    return formikHolidayEdit.values.holidays.length > 0;
  }, [formikHolidayEdit.values.holidays]);

  const handleOpenDeleteCategoryPopup = (coursing: IMeetingCategory) => {
    setCategoryToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeleteCategoryPopup = () => {
    setCategoryToDelete(null);
    setOpenDeleteCategory(false);
  };

  const handleOpenDeleteHolidayPopup = (coursing: IMeetingHoliday) => {
    setHolidayToDelete(coursing);
    setOpenDeleteHoliday(true);
  };

  const handleCloseDeleteHolidayPopup = () => {
    setHolidayToDelete(null);
    setOpenDeleteHoliday(false);
  };

  const handleOpenEditCategoryPopup = (coursing: IMeetingCategory) => {
    setCategoryToEdit(coursing);
    setOpenEditCategory(true);
  };

  const handleOpenEditHolidayPopup = (coursing: IMeetingHoliday) => {
    setCategoryToEditHoliday(coursing);
    setOpenEditHoliday(true);
  };

  const handleClose = () => {
    setOpenEditCategory(false);
    setCategoryToEdit(null);
  };

  const handleCloseHoliday = () => {
    setOpenEditHoliday(false);
    setCategoryToEditHoliday(null);
  };

  const handleRemoveDate = (dateToRemove: DateObject) => {
    const newDates = formikHoliday.values.holidays.filter(
      (date) => date.format("YYYY-MM-DD") !== dateToRemove.format("YYYY-MM-DD")
    );
    formikHoliday.setFieldValue("holidays", newDates);
  };

  const handleRemoveEditDate = (dateToRemove: DateObject) => {
    const newDates = formikHolidayEdit.values.holidays.filter(
      (date) => date.format("YYYY-MM-DD") !== dateToRemove.format("YYYY-MM-DD")
    );
    formikHolidayEdit.setFieldValue("holidays", newDates);
  };

  const [deleteCategory] = useDeleteMeetingCategoryMutation();
  const handleDeleteCategory = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
        handleClose();
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the meeting category");
    }
  };

  const [deleteHoliday] = useDeleteMeetingHolidayMutation();
  const handleDeleteHoliday = async (id: string) => {
    try {
      const response = await deleteHoliday(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteHoliday(false);
        handleCloseHoliday();
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the meeting holiday");
    }
  };

  const meetingCategoryList: IMeetingCategory[] = Array.isArray(
    getAllMeetingCategoryData?.data
  )
    ? (getAllMeetingCategoryData?.data as IMeetingCategory[])
    : [];

  const meetingHolidayList: IMeetingHoliday[] = Array.isArray(
    getAllMeetingHolidayData?.data
  )
    ? (getAllMeetingHolidayData?.data as IMeetingHoliday[])
    : [];

  if (
    meetingCategoryIsLoading ||
    meetingHolidayIsLoading ||
    holidaysIsLoading
  ) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={6} md={12} sm={12} xs={12}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              backgroundColor: theme.palette.cardAdd.bgColor,
              // p: "24px",
              borderRadius: setCardBRadius,
              // minHeight: "100vh",
            }}
          >
            <form onSubmit={formik.handleSubmit}>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Grid
                    container
                    justifyContent="space-between"
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid item xs={12} pt={2} alignContent="center">
                      <Typography className={classes.cardTitle}>
                        Category
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} pb={8} pt={3}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container spacing={2} px={3} alignItems="flex-end">
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" mb={1}>
                            Color{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FormControl
                              size="small"
                              variant="outlined"
                              sx={{
                                width: 150,
                                "& .MuiOutlinedInput-root": {
                                  paddingRight: "8px",
                                  fontSize: 14,
                                },
                                '& input[type="color"]': {
                                  width: 24,
                                  height: 24,
                                  border: "none",
                                  padding: 0,
                                  background: "transparent",
                                  cursor: "pointer",
                                },
                              }}
                            >
                              <OutlinedInput
                                id="outlined-adornment-color"
                                value={formik.values.color}
                                onChange={(e) =>
                                  formik.setFieldValue("color", e.target.value)
                                }
                                endAdornment={
                                  <InputAdornment position="end" sx={{ ml: 1 }}>
                                    <input
                                      type="color"
                                      value={formik.values.color}
                                      onChange={(e) =>
                                        formik.setFieldValue(
                                          "color",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </InputAdornment>
                                }
                                aria-describedby="outlined-color-helper-text"
                                inputProps={{
                                  "aria-label": "color",
                                }}
                              />
                            </FormControl>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" mb={1}>
                            Name <span className={classes.requiedfield}>*</span>
                          </Typography>
                          <TextField
                            placeholder="Name"
                            size="small"
                            fullWidth
                            multiline
                            rows={6}
                            {...formik.getFieldProps("name")}
                            className={classes.textField}
                            InputProps={{
                              sx: { fontSize: 14, minWidth: 200 },
                            }}
                            InputLabelProps={{ sx: { fontSize: 14 } }}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={formik.handleReset}
                            disabled={createCategoryIsLoading}
                          >
                            Reset
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              createCategoryIsLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid || createCategoryIsLoading}
                          >
                            Create
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} p={3}>
                    {meetingCategoryIsLoading ? (
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="10vh"
                        >
                          <CircularProgress />
                        </Box>
                      </Grid>
                    ) : meetingCategoryIsError ||
                      meetingCategoryList.length === 0 ? (
                      <Grid item xs={12}>
                        <Typography textAlign="center">
                          {meetingCategoryIsError
                            ? "Error fetching data"
                            : "No data available"}
                        </Typography>
                      </Grid>
                    ) : (
                      meetingCategoryList
                        .slice(
                          page1 * rowsPerPage1,
                          page1 * rowsPerPage1 + rowsPerPage1
                        )
                        .map((row: IMeetingCategory) => (
                          <Grid item xs={12} md={12} lg={12} key={row.id}>
                            <Card
                              variant="outlined"
                              // sx={{ borderRadius: 2 }}
                              elevation={1}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                height: "100%",
                                backgroundColor: theme.palette.cardAdd.bgColor,
                                // backgroundColor: "#fafafa",
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: row.color,
                                    borderRadius: "50%",
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <Typography fontWeight="bold" fontSize={16}>
                                  {row.name}
                                </Typography>
                              </Box>

                              <Box display="flex" alignItems="center" gap={2}>
                                <Box display="flex" gap={1} ml="auto">
                                  <Button
                                    className={classes.editButtonList}
                                    onClick={() =>
                                      handleOpenEditCategoryPopup(row)
                                    }
                                  >
                                    <EditSharpIcon
                                      className={classes.editListIcon}
                                    />
                                  </Button>
                                  <Button
                                    className={classes.deleteButtonList}
                                    onClick={() =>
                                      handleOpenDeleteCategoryPopup(row)
                                    }
                                  >
                                    <DeleteOutlineSharpIcon
                                      className={classes.editListIcon}
                                    />
                                  </Button>
                                </Box>
                              </Box>
                            </Card>
                          </Grid>
                        ))
                    )}
                  </Grid>
                </Grid>

                {categoryToDelete && (
                  <DeletePopup
                    open={openDeleteCategory}
                    handleCloseDelete={handleCloseDeleteCategoryPopup}
                    onConfirm={async () => {
                      await handleDeleteCategory(
                        categoryToDelete.id.toString()
                      );
                    }}
                    title="Delete Meeting Category"
                    content={`Are you sure you want to delete "${categoryToDelete.name}"?`}
                  />
                )}
              </Grid>
            </form>
          </Card>
          <Dialog
            open={openEditCategory}
            onClose={() => setOpenEditCategory(false)}
            aria-describedby="alert-dialog-description"
            classes={{ paper: "inherit" }}
            fullScreen={isMobile}
            fullWidth={isMobile}
            maxWidth="lg"
          >
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <form onSubmit={formikEditCategory.handleSubmit}>
                    <Grid container>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          justifyContent="space-between"
                          spacing={5}
                          sx={{ borderBottom: 2, padding: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid item xs={12} pt={2} alignContent="center">
                            <Typography className={classes.cardTitle}>
                              Edit
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} pb={8} pt={3}>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid
                              container
                              spacing={2}
                              px={3}
                              alignItems="flex-end"
                            >
                              <Grid item xs={12}>
                                <Typography variant="subtitle1" mb={1}>
                                  Color{" "}
                                  <span className={classes.requiedfield}>
                                    *
                                  </span>
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <FormControl
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      width: 150,
                                      "& .MuiOutlinedInput-root": {
                                        paddingRight: "8px",
                                        fontSize: 14,
                                      },
                                      '& input[type="color"]': {
                                        width: 24,
                                        height: 24,
                                        border: "none",
                                        padding: 0,
                                        background: "transparent",
                                        cursor: "pointer",
                                      },
                                    }}
                                  >
                                    <OutlinedInput
                                      id="outlined-adornment-color"
                                      value={formikEditCategory.values.color}
                                      onChange={(e) =>
                                        formikEditCategory.setFieldValue(
                                          "color",
                                          e.target.value
                                        )
                                      }
                                      endAdornment={
                                        <InputAdornment
                                          position="end"
                                          sx={{ ml: 1 }}
                                        >
                                          <input
                                            type="color"
                                            value={
                                              formikEditCategory.values.color
                                            }
                                            onChange={(e) =>
                                              formikEditCategory.setFieldValue(
                                                "color",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </InputAdornment>
                                      }
                                      aria-describedby="outlined-color-helper-text"
                                      inputProps={{
                                        "aria-label": "color",
                                      }}
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="subtitle1" mb={1}>
                                  Name{" "}
                                  <span className={classes.requiedfield}>
                                    *
                                  </span>
                                </Typography>
                                <TextField
                                  placeholder="Name"
                                  size="small"
                                  fullWidth
                                  multiline
                                  rows={6}
                                  {...formikEditCategory.getFieldProps("name")}
                                  className={classes.textField}
                                  InputProps={{
                                    sx: {
                                      fontSize: 14,
                                      minWidth: isMobile ? undefined : 700,
                                    },
                                  }}
                                  InputLabelProps={{ sx: { fontSize: 14 } }}
                                />
                              </Grid>
                              <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  startIcon={<CloseIcon />}
                                  onClick={handleClose}
                                  disabled={createCategoryIsLoading}
                                >
                                  Reset
                                </Button>
                                <Box m={0.5}></Box>

                                <Button
                                  variant="contained"
                                  startIcon={
                                    createCategoryIsLoading ? (
                                      <CircularProgress
                                        size={18}
                                        className={classes.isLoading}
                                      />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  className={classes.addButton}
                                  onClick={() =>
                                    formikEditCategory.handleSubmit()
                                  }
                                  disabled={
                                    !formEditCategoryValid ||
                                    createCategoryIsLoading
                                  }
                                >
                                  Update
                                </Button>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </Card>
              </Grid>
            </Grid>
          </Dialog>
        </Grid>
        <Grid item lg={6} md={12} sm={12} xs={12}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              backgroundColor: theme.palette.cardAdd.bgColor,
              // p: "24px",
              borderRadius: setCardBRadius,
              // minHeight: "100vh",
            }}
          >
            <form onSubmit={formikHoliday.handleSubmit}>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Grid
                    container
                    justifyContent="space-between"
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid item xs={12} pt={2} alignContent="center">
                      <Typography className={classes.cardTitle}>
                        Holiday
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} pb={8} pt={3}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container spacing={2} px={3} alignItems="flex-end">
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" mb={1}>
                            Holidays{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>

                          <DatePicker
                            multiple
                            value={formikHoliday.values.holidays}
                            onChange={(dates: DateObject[]) => {
                              formikHoliday.setFieldValue("holidays", dates);
                            }}
                            format="YYYY-MM-DD"
                            mapDays={({ date: day }) => {
                              const isDisabled = holydaysData?.data?.some(
                                (holiday) =>
                                  dayjs(day.toDate()).isSame(holiday, "day")
                              );

                              if (isDisabled) {
                                return {
                                  disabled: true,
                                  style: {
                                    backgroundColor: "#d8d8d8",
                                    color: "#721c24",
                                  },
                                };
                              }

                              return {};
                            }}
                            render={(value, openCalendar) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  // minHeight: 56,
                                  border: "1px solid #ccc",
                                  borderRadius: 1,
                                  px: 1.5,
                                  py: 1,
                                  cursor: "pointer",
                                  width: "100%",
                                  "&:hover": { borderColor: "#1976d2" },
                                }}
                                onClick={openCalendar}
                              >
                                {formikHoliday.values.holidays.length === 0 ? (
                                  <Typography
                                    sx={{
                                      flexGrow: 1,
                                      color: "gray",
                                      fontSize: 14,
                                    }}
                                  >
                                    Select Holiday Dates
                                  </Typography>
                                ) : (
                                  formikHoliday.values.holidays.map(
                                    (date, idx) => (
                                      <Chip
                                        key={idx}
                                        label={date.format("YYYY-MM-DD")}
                                        onDelete={(e) => {
                                          e.stopPropagation(); // Prevent calendar open
                                          handleRemoveDate(date);
                                        }}
                                        sx={{ m: 0.5 }}
                                      />
                                    )
                                  )
                                )}

                                <Box sx={{ marginLeft: "auto" }}>
                                  <IconButton
                                    onClick={openCalendar}
                                    size="small"
                                  >
                                    <CalendarTodayIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                          />
                        </Grid>

                        {/* <Grid item xs={12}>
                          <Typography variant="subtitle1" mb={1}>
                            Holidays{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>

                          <DatePicker
                            multiple
                            value={formikHoliday.values.holidays}
                            onChange={(dates: DateObject[]) => {
                              formikHoliday.setFieldValue(
                                "holidays",
                                dates
                              );
                            }}
                            format="YYYY-MM-DD"
                            render={(value, openCalendar) => (
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Schedule Date"
                                value={value}
                                onClick={openCalendar}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={openCalendar}
                                        size="small"
                                      >
                                        <CalendarTodayIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />

                          {formikHoliday.values.holidays.length > 0 && (
                            <Grid container spacing={1} mt={1}>
                              {formikHoliday.values.holidays.map(
                                (date, idx) => (
                                  <Grid item key={idx}>
                                    <Chip
                                      label={date.format("YYYY-MM-DD")}
                                      onDelete={() => handleRemoveDate(date)}
                                      color="primary"
                                    />
                                  </Grid>
                                )
                              )}
                            </Grid>
                          )}
                        </Grid> */}

                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1" mb={1}>
                            Remark
                          </Typography>
                          <TextField
                            placeholder="Remark"
                            size="small"
                            fullWidth
                            multiline
                            rows={5}
                            {...formikHoliday.getFieldProps("note")}
                            className={classes.textField}
                            InputProps={{
                              sx: { fontSize: 14, minWidth: 200 },
                            }}
                            InputLabelProps={{ sx: { fontSize: 14 } }}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={formikHoliday.handleReset}
                            disabled={createCategoryIsLoading}
                          >
                            Reset
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              createHolidayIsLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formikHoliday.handleSubmit()}
                            disabled={
                              !formValidHoliday || createHolidayIsLoading
                            }
                          >
                            Create
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} p={3}>
                    {meetingHolidayIsLoading ? (
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="10vh"
                        >
                          <CircularProgress />
                        </Box>
                      </Grid>
                    ) : meetingHolidayIsError ||
                      meetingHolidayList.length === 0 ? (
                      <Grid item xs={12}>
                        <Typography textAlign="center">
                          {meetingHolidayIsError
                            ? "Error fetching data"
                            : "No data available"}
                        </Typography>
                      </Grid>
                    ) : (
                      meetingHolidayList
                        .slice(
                          page2 * rowsPerPage2,
                          page2 * rowsPerPage2 + rowsPerPage2
                        )
                        .map((row: IMeetingHoliday) => (
                          <Grid item xs={12} md={12} lg={12} key={row.id}>
                            <Card
                              variant="outlined"
                              elevation={1}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                height: "100%",
                                backgroundColor: theme.palette.cardAdd.bgColor,
                              }}
                            >
                              {row.holidays.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {row.holidays.map((date, idx) => (
                                    <Chip
                                      key={idx}
                                      label={date}
                                      size="small"
                                      sx={{
                                        fontSize: 13,
                                        backgroundColor: "#f5f5f5",
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              <Typography fontSize={14} color="text.secondary">
                                {row.note}
                              </Typography>

                              <Box display="flex" alignItems="center" gap={2}>
                                <Box display="flex" gap={1} ml="auto">
                                  <Button
                                    className={classes.editButtonList}
                                    onClick={() =>
                                      handleOpenEditHolidayPopup(row)
                                    }
                                  >
                                    <EditSharpIcon
                                      className={classes.editListIcon}
                                    />
                                  </Button>
                                  <Button
                                    className={classes.deleteButtonList}
                                    onClick={() =>
                                      handleOpenDeleteHolidayPopup(row)
                                    }
                                  >
                                    <DeleteOutlineSharpIcon
                                      className={classes.editListIcon}
                                    />
                                  </Button>
                                </Box>
                              </Box>
                            </Card>
                          </Grid>
                        ))
                    )}
                  </Grid>
                </Grid>

                {openDeleteHoliday && holidayToDelete && (
                  <DeletePopup
                    open={openDeleteHoliday}
                    handleCloseDelete={handleCloseDeleteHolidayPopup}
                    onConfirm={async () => {
                      await handleDeleteHoliday(holidayToDelete.id.toString());
                    }}
                    title="Delete Meeting Holidays"
                    content={`Are you sure you want to delete "${holidayToDelete.note}"?`}
                  />
                )}
              </Grid>
            </form>
          </Card>
          <Dialog
            open={openEditHoliday}
            onClose={() => setOpenEditHoliday(false)}
            aria-describedby="alert-dialog-description"
            classes={{ paper: "inherit" }}
            fullScreen={isMobile}
            fullWidth={isMobile}
            maxWidth="lg"
          >
            <Grid container spacing={3}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <form onSubmit={formikHolidayEdit.handleSubmit}>
                    <Grid container>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          justifyContent="space-between"
                          spacing={5}
                          sx={{ borderBottom: 2, padding: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid item xs={12} pt={2} alignContent="center">
                            <Typography className={classes.cardTitle}>
                              Holiday
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} pb={8} pt={3}>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid
                              container
                              spacing={2}
                              px={3}
                              alignItems="flex-end"
                            >
                              <Grid item xs={12}>
                                <Typography variant="subtitle1" mb={1}>
                                  Holidays{" "}
                                  <span className={classes.requiedfield}>
                                    *
                                  </span>
                                </Typography>

                                <DatePicker
                                  multiple
                                  value={formikHolidayEdit.values.holidays}
                                  onChange={(dates: DateObject[]) => {
                                    formikHolidayEdit.setFieldValue(
                                      "holidays",
                                      dates
                                    );
                                  }}
                                  format="YYYY-MM-DD"
                                  render={(value, openCalendar) => (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        // minHeight: 56,
                                        border: "1px solid #ccc",
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1,
                                        cursor: "pointer",
                                        width: "100%",
                                        "&:hover": {
                                          borderColor: "#1976d2",
                                        },
                                      }}
                                      onClick={openCalendar}
                                    >
                                      {formikHolidayEdit.values.holidays
                                        .length === 0 ? (
                                        <Typography
                                          sx={{
                                            flexGrow: 1,
                                            color: "gray",
                                            fontSize: 14,
                                          }}
                                        >
                                          Select Holiday Dates
                                        </Typography>
                                      ) : (
                                        formikHolidayEdit.values.holidays.map(
                                          (date, idx) => (
                                            <Chip
                                              key={idx}
                                              label={date.format("YYYY-MM-DD")}
                                              onDelete={(e) => {
                                                e.stopPropagation(); // Prevent calendar open
                                                handleRemoveEditDate(date);
                                              }}
                                              sx={{ m: 0.5 }}
                                            />
                                          )
                                        )
                                      )}

                                      <Box sx={{ marginLeft: "auto" }}>
                                        <IconButton
                                          onClick={openCalendar}
                                          size="small"
                                        >
                                          <CalendarTodayIcon />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  )}
                                />
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1" mb={1}>
                                  Remark
                                </Typography>
                                <TextField
                                  placeholder="Remark"
                                  size="small"
                                  fullWidth
                                  multiline
                                  rows={5}
                                  {...formikHolidayEdit.getFieldProps("note")}
                                  className={classes.textField}
                                  InputProps={{
                                    sx: { fontSize: 14, minWidth: 200 },
                                  }}
                                  InputLabelProps={{ sx: { fontSize: 14 } }}
                                />
                              </Grid>
                              <Grid
                                item
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  startIcon={<CloseIcon />}
                                  onClick={() => setOpenEditHoliday(false)}
                                  disabled={createCategoryIsLoading}
                                >
                                  Cancel
                                </Button>
                                <Box m={0.5}></Box>

                                <Button
                                  variant="contained"
                                  startIcon={
                                    createHolidayIsLoading ? (
                                      <CircularProgress
                                        size={18}
                                        className={classes.isLoading}
                                      />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  className={classes.addButton}
                                  onClick={() =>
                                    formikHolidayEdit.handleSubmit()
                                  }
                                  disabled={
                                    !formValidHolidayEdit ||
                                    createHolidayIsLoading
                                  }
                                >
                                  Update
                                </Button>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </Card>
              </Grid>
            </Grid>
          </Dialog>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
